import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api, API, getToken, formatMoney, tzOffset } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AddExpenseModal from "@/components/AddExpenseModal";
import PeriodPicker, { usePeriod, periodLabel } from "@/components/PeriodPicker";
import ItemwiseReport from "@/components/ItemwiseReport";
import ClosingReportModal from "@/components/ClosingReportModal";
import { Banknote, CreditCard, Smartphone, TrendingUp, Receipt, Download, FileText, Wallet, Trash2, Plus, ShoppingBasket, Calculator } from "lucide-react";

const m = (n) => `₹${formatMoney(n)}`;

export default function Reports() {
  const { outlet } = useOutletContext();
  const { role, userName } = useAuth();
  const [period, setPeriod] = usePeriod();
  const [tab, setTab] = useState("summary");
  const [report, setReport] = useState(null);
  const [orders, setOrders] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);

  const load = useCallback(() => {
    if (!outlet) return;
    const params = `?outlet_id=${outlet.id}&date_from=${period.from}&date_to=${period.to}&tz_offset=${tzOffset()}`;
    Promise.all([api.get(`/reports/daily${params}`), api.get(`/orders${params}`)]).then(([r, o]) => {
      setReport(r.data);
      setOrders(o.data);
    });
  }, [outlet, period]);

  useEffect(load, [load]);

  const exportCsv = async () => {
    const url = `${API}/orders.csv?outlet_id=${outlet.id}&date_from=${period.from}&date_to=${period.to}&tz_offset=${tzOffset()}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) return toast.error("CSV export failed");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = `teaboy-${outlet.name.replace(/\s+/g, "-").toLowerCase()}-${period.from}_${period.to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  if (!outlet || !report) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const canAdd = role === "owner" || role === "cashier";

  const isRange = period.from !== period.to;

  return (
    <div className="readable h-full overflow-y-auto thin-scroll" data-testid="reports-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight">Reports</h1>
            <p className="text-base text-muted-foreground mt-1" data-testid="report-period-label">{outlet.name} · {periodLabel(period)}</p>
          </div>
          <PeriodPicker value={period} onChange={setPeriod} testid="report-period" />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 border-b border-border">
          <div className="flex gap-1">
            {[{ id: "summary", label: "Summary" }, { id: "items", label: "Item-wise" }].map((t) => (
              <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {t.label}
              </button>
            ))}
          </div>
          {tab === "summary" && (
            <div className="flex items-center gap-2 flex-wrap pb-2">
              {canAdd && (
                <ActionBtn testid="report-add-expense-btn" onClick={() => setExpenseOpen(true)} icon={<Plus className="w-4 h-4" />}>Expense</ActionBtn>
              )}
              <ActionBtn testid="report-csv-btn" onClick={exportCsv} icon={<Download className="w-4 h-4" />}>CSV</ActionBtn>
              <button data-testid="day-end-report-btn" onClick={() => setClosingOpen(true)}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-transform">
                <FileText strokeWidth={2} className="w-4 h-4" /> {isRange ? "Period Summary" : "Day End Report"}
              </button>
            </div>
          )}
        </div>

        {tab === "items" ? (
          <ItemwiseReport outlet={outlet} period={period} printedBy={userName} />
        ) : (
          <>
            <KpiGrid r={report} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PaymentSplit r={report} />
              <ExpensesPanel r={report} showDate={isRange} canDelete={role === "owner"} onDelete={deleteExpense} onAdd={canAdd ? () => setExpenseOpen(true) : null} />
            </div>
            {isRange && <DailyList daily={report.daily} />}
            <TopItems items={report.top_items} />
            <OrdersList orders={orders} showDate={isRange} />
          </>
        )}
        <div className="pb-12" />
      </div>

      {expenseOpen && <AddExpenseModal outlet={outlet} onClose={() => setExpenseOpen(false)} onAdded={load} />}
      {closingOpen && <ClosingReportModal report={report} printedBy={userName} onClose={() => setClosingOpen(false)} />}
    </div>
  );
}

function ActionBtn({ testid, onClick, icon, children }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex items-center gap-2 border border-border bg-card px-3.5 py-2.5 text-sm font-semibold hover:bg-muted active:scale-[0.98] transition-transform">
      {icon} {children}
    </button>
  );
}

function KpiGrid({ r }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="col-span-2 bg-[hsl(var(--brand-plate))] text-primary-foreground p-6 flex flex-col justify-between animate-fade-up" data-testid="stat-revenue">
        <div className="flex items-center gap-2 text-sm font-semibold opacity-75"><TrendingUp className="w-4 h-4" /> Gross sales</div>
        <div className="text-5xl font-extrabold mt-3 tabular-nums">{m(r.revenue)}</div>
        <div className="text-sm opacity-75 mt-2">Net after expenses · <span className="font-bold opacity-100">{m(r.net_total)}</span></div>
      </div>
      <Stat testid="stat-orders" icon={<Receipt />} label="Orders" value={r.order_count} />
      <Stat testid="stat-avg" icon={<Calculator />} label="Average bill" value={m(r.avg_ticket)} />
      <Stat testid="stat-items" icon={<ShoppingBasket />} label="Items sold" value={r.items_sold} />
      <Stat testid="stat-expenses" icon={<Wallet />} label="Expenses" value={m(r.expense_total)} tone="text-destructive" />
      <div className="col-span-2 bg-primary/10 border-2 border-primary p-5 flex items-center justify-between" data-testid="stat-cash-in-hand">
        <div>
          <div className="text-sm font-bold text-primary">Cash in hand</div>
          <div className="text-xs text-muted-foreground mt-0.5">Cash sales − expenses</div>
        </div>
        <div className={`text-4xl font-extrabold tabular-nums ${r.net_cash < 0 ? "text-destructive" : ""}`}>{m(r.net_cash)}</div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testid, tone = "" }) {
  return (
    <div className="bg-card border border-border p-5 animate-fade-up" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm font-semibold">
        <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{icon}</span> {label}
      </div>
      <div className={`text-3xl font-extrabold mt-2 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function PaymentSplit({ r }) {
  const rows = [
    { key: "cash", label: "Cash", icon: <Banknote className="w-5 h-5" />, v: r.cash_total },
    { key: "card", label: "Card", icon: <CreditCard className="w-5 h-5" />, v: r.card_total },
    { key: "upi", label: "UPI", icon: <Smartphone className="w-5 h-5" />, v: r.upi_total },
  ];
  return (
    <section className="bg-card border border-border p-6" data-testid="payment-split">
      <h2 className="text-lg font-bold mb-4">Payment split</h2>
      <div className="space-y-4">
        {rows.map((x) => {
          const pct = r.revenue ? Math.round((x.v / r.revenue) * 100) : 0;
          return (
            <div key={x.key} data-testid={`stat-${x.key}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold">{x.icon} {x.label}</span>
                <span className="text-xl font-extrabold tabular-nums">{m(x.v)} <span className="text-xs text-muted-foreground font-semibold">{pct}%</span></span>
              </div>
              <div className="h-2 bg-muted mt-2"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const whenText = (iso, showDate) =>
  showDate
    ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function DailyList({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.revenue));
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">Day-by-day</h2>
      <div className="bg-card border border-border divide-y divide-border max-h-96 overflow-y-auto thin-scroll" data-testid="daily-list">
        {daily.map((d) => (
          <div key={d.date} className="px-5 py-2.5 flex items-center gap-4">
            <span className="w-28 text-sm font-semibold">{new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}</span>
            <div className="flex-1 h-2 bg-muted"><div className="h-full bg-primary/70" style={{ width: `${(d.revenue / max) * 100}%` }} /></div>
            <span className="w-20 text-right text-sm text-muted-foreground">{d.orders} orders</span>
            <span className="w-24 text-right text-sm text-destructive tabular-nums">{d.expenses ? `−${m(d.expenses)}` : ""}</span>
            <span className="w-24 text-right font-bold tabular-nums">{m(d.revenue)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ExpensesPanel({ r, canDelete, onDelete, onAdd, showDate }) {
  return (
    <section className="bg-card border border-border p-6 flex flex-col" data-testid="expenses-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Expenses</h2>
        <span className="text-xl font-extrabold text-destructive tabular-nums" data-testid="expenses-total">{m(r.expense_total)}</span>
      </div>
      {r.expenses.length === 0 ? (
        <div className="flex-1 border border-dashed border-border p-6 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-3">
          No expenses recorded for this period.
          {onAdd && <button data-testid="expenses-empty-add-btn" onClick={onAdd} className="text-primary font-bold hover:underline">+ Add expense</button>}
        </div>
      ) : (
        <div className="divide-y divide-border" data-testid="expenses-list">
          {r.expenses.map((e) => (
            <div key={e.id} className="py-2.5 flex items-center gap-3" data-testid={`expense-row-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{e.description}</div>
                <div className="text-xs text-muted-foreground">
                  {whenText(e.created_at, showDate)} · {e.created_by}
                </div>
              </div>
              <div className="font-bold tabular-nums">{m(e.amount)}</div>
              {canDelete && (
                <button data-testid={`expense-delete-${e.id}`} onClick={() => onDelete(e.id)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TopItems({ items }) {
  const max = Math.max(1, ...items.map((i) => i.qty));
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">Top items</h2>
      {items.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center">No sales for this period.</div>
      ) : (
        <div className="bg-card border border-border divide-y divide-border" data-testid="top-items">
          {items.map((it, i) => (
            <div key={it.name} className="px-5 py-3 flex items-center gap-4">
              <span className="w-6 text-sm font-bold text-muted-foreground">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{it.name}</div>
                <div className="h-1.5 bg-muted mt-1.5"><div className="h-full bg-primary/70" style={{ width: `${(it.qty / max) * 100}%` }} /></div>
              </div>
              <span className="text-sm text-muted-foreground w-16 text-right">{it.qty} sold</span>
              <span className="font-bold w-24 text-right tabular-nums">{m(it.revenue)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function OrdersList({ orders, showDate }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">Orders <span className="text-muted-foreground font-semibold text-base">({orders.length})</span></h2>
      {orders.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center">No orders for this period.</div>
      ) : (
        <div className="bg-card border border-border divide-y divide-border" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="px-5 py-3" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{whenText(o.created_at, showDate)}</span>
                  <span className="text-xs font-bold uppercase bg-muted px-2 py-0.5">{o.payment_method}</span>
                  <span className="text-xs text-muted-foreground">#{o.id.slice(0, 8).toUpperCase()} · {o.cashier_name}</span>
                </div>
                <span className="text-lg font-extrabold tabular-nums">{m(o.total)}</span>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{o.items.map((it) => `${it.qty}× ${it.name}`).join(" · ")}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
